import { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverButton, PopoverPanel } from "@headlessui/react";
import { format, isValid, parse } from "date-fns";
import { RegisterOptions, useController } from "react-hook-form";
import { HiOutlineCalendarDays } from "react-icons/hi2";

import useClickOutside from "@/hooks/useClickOutside";
import useUserLocale from "@/hooks/useUserLocale";
import { Calendar } from "@/components/atoms/Calendar";
import Input from "@/components/atoms/input/Input";
import FormControl from "@/components/molecules/form/FormControl";

type Props = {
  name: string;
  rules?: RegisterOptions;
  label?: string;
};

const FormDatePicker = ({ label, name, rules }: Props) => {
  const {
    field: { onChange, ref, value },
    fieldState: { error },
  } = useController({
    name,
    rules,
    defaultValue: "",
  });

  const { format: dateFormat, locale: dateFnsLocale, placeholder } = useUserLocale();

  const [isOpen, setIsOpen] = useState(false);
  const [inputValue, setInputValue] = useState<string>(
    value === "" ? "" : format(value, dateFormat, { locale: dateFnsLocale }),
  );
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [month, setMonth] = useState(new Date());

  const panelRef = useRef<HTMLButtonElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const inputWidth = useMemo(() => inputRef.current?.clientWidth || 0, [inputRef.current?.clientWidth]);

  useClickOutside([panelRef, inputRef], () => {
    if (isOpen) {
      setIsOpen(false);
    }
  });

  const handleDayPickerSelect = (date: Date | undefined) => {
    if (!date) {
      setInputValue("");
      setSelectedDate(undefined);
      onChange("");
    } else {
      setSelectedDate(date);
      setMonth(date);
      setInputValue(format(date, dateFormat, { locale: dateFnsLocale }));
      onChange(date.toISOString());
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);

    const parsedDate = parse(e.target.value, dateFormat, new Date(), { locale: dateFnsLocale });

    if (isValid(parsedDate)) {
      setSelectedDate(parsedDate);
      setMonth(parsedDate);
      onChange(parsedDate);
    } else {
      setSelectedDate(undefined);
      onChange("");
    }
  };

  // Reset Datepicker view to current month
  useEffect(() => {
    if (inputValue === "") {
      setMonth(new Date());
    }
  }, [inputValue]);

  return (
    <Popover className={"relative flex w-60"}>
      {/* Popover Button is needed to trigger the popover, even when it is empty. */}
      <PopoverButton />
      <div ref={inputRef} onClick={() => setIsOpen(true)}>
        <FormControl label={label} id={name} error={error?.message}>
          <Input
            id={name}
            type="text"
            ref={ref}
            leftPlaceholder={<HiOutlineCalendarDays className={"size-5 text-fg-quaternary"} />}
            value={inputValue}
            hasError={!!error}
            placeholder={placeholder}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (isOpen && e.key === "Escape") {
                setIsOpen(false);
              }
            }}
          />
        </FormControl>
      </div>
      {isOpen && (
        <PopoverPanel
          className="w-fit p-0"
          static
          ref={panelRef}
          anchor={{ to: "right", gap: `${inputWidth + 8}px`, offset: "5px" }}
        >
          <Calendar
            locale={dateFnsLocale}
            mode="single"
            month={month}
            fixedWeeks
            showYearSwitcher={false}
            onMonthChange={setMonth}
            selected={selectedDate}
            onSelect={handleDayPickerSelect}
            onReset={() => handleDayPickerSelect(undefined)}
            onClose={() => setIsOpen(false)}
            disabled={{
              before: new Date("1900-01-01"),
              after: new Date(),
            }}
          />
        </PopoverPanel>
      )}
    </Popover>
  );
};

export default FormDatePicker;
